import { useCallback } from 'react';
import { message } from 'antd';
import type { Editor } from '@tiptap/react';

const PLACEHOLDER = 'data:image/svg+xml;utf8,uploading';

/** 在文件中找到指定暫存 src 的 image node，回傳其位置。 */
function findNodePos(editor: Editor, marker: string): number | null {
  let pos: number | null = null;
  editor.state.doc.descendants((node, p) => {
    if (node.type.name === 'image' && node.attrs.src === marker) pos = p;
  });
  return pos;
}

/** 上傳單一檔案：先插佔位，成功換成 R2 url，失敗移除佔位。可並行多檔。 */
async function uploadOne(
  editor: Editor,
  file: File,
  insertAt: number | null,
  uploadImage: (f: File) => Promise<{ url: string }>,
  onUploaded?: () => void,
) {
  const marker = `${PLACEHOLDER}#${crypto.randomUUID()}`;
  const node = { type: 'image', attrs: { src: marker, uploading: true, align: 'center' } };
  if (insertAt == null) editor.chain().focus().insertContent(node).run();
  else editor.chain().insertContentAt(insertAt, node).run();

  try {
    const { url } = await uploadImage(file);
    const pos = findNodePos(editor, marker);
    if (pos == null) return;
    editor.chain().setNodeSelection(pos).updateAttributes('image', { src: url, uploading: false }).run();
    // 上傳圖片是「重量級」動作，馬上存一次，不等自動儲存的 debounce——
    // 記者常在上傳完就切走，等 1.8 秒會讓人以為文章不見了。
    // setTimeout 0：等 onUpdate 造成的 state 更新 commit 後再存，才存得到新內容。
    if (onUploaded) setTimeout(onUploaded, 0);
  } catch (e) {
    const pos = findNodePos(editor, marker);
    if (pos != null) editor.chain().setNodeSelection(pos).deleteSelection().run();
    message.error(e instanceof Error ? e.message : '圖片上傳失敗');
  }
}

/** 取得圖片中的所有檔案（拖放 / 貼上共用）。 */
function imageFiles(list?: FileList | null, items?: DataTransferItemList): File[] {
  const out: File[] = [];
  if (list) for (const f of Array.from(list)) if (f.type.startsWith('image/')) out.push(f);
  if (!out.length && items)
    for (const it of Array.from(items))
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) out.push(f);
      }
  return out;
}

/** 提供 editorProps 的 handlePaste / handleDrop，將圖片上傳 R2 後插入。 */
export function useImageUpload(
  uploadImage: (f: File) => Promise<{ url: string }>,
  onUploaded?: () => void,
) {
  const run = useCallback(
    (editor: Editor, files: File[], pos: number | null) => {
      for (const f of files) void uploadOne(editor, f, pos, uploadImage, onUploaded);
    },
    [uploadImage, onUploaded],
  );

  const handlePaste = useCallback(
    (editor: Editor, event: ClipboardEvent): boolean => {
      const files = imageFiles(event.clipboardData?.files, event.clipboardData?.items);
      if (!files.length) return false;
      event.preventDefault();
      run(editor, files, null);
      return true;
    },
    [run],
  );

  const handleDrop = useCallback(
    (editor: Editor, event: DragEvent): boolean => {
      const files = imageFiles(event.dataTransfer?.files, event.dataTransfer?.items);
      if (!files.length) return false;
      event.preventDefault();
      const coords = editor.view.posAtCoords({ left: event.clientX, top: event.clientY });
      run(editor, files, coords?.pos ?? null);
      return true;
    },
    [run],
  );

  /** 工具列「插入圖片」按鈕共用同一條上傳路徑（含佔位）。 */
  const insertFiles = useCallback(
    (editor: Editor, files: File[]) => run(editor, files.filter((f) => f.type.startsWith('image/')), null),
    [run],
  );

  return { handlePaste, handleDrop, insertFiles };
}
